import React, { useState, useEffect, useRef } from 'react';
import type { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheck, FiX, FiAlertTriangle, FiInfo } from 'react-icons/fi';

export interface ValidationRule {
  field: string;
  validator: (value: any, formData?: Record<string, any>) => ValidationResult;
  trigger?: 'blur' | 'change' | 'submit';
  debounceMs?: number;
}

export interface ValidationResult {
  isValid: boolean;
  level?: 'error' | 'warning' | 'info' | 'success';
  message?: string;
  suggestions?: string[];
}

export interface ValidationState {
  [field: string]: ValidationResult;
}

interface AdvancedValidationProps {
  rules: ValidationRule[];
  formData: Record<string, any>;
  onValidationChange?: (validationState: ValidationState) => void;
  showSuccessStates?: boolean;
  showInlineMessages?: boolean;
  className?: string;
  zodSchema?: z.ZodTypeAny; // optional Zod schema to compose with rules
}

const AdvancedValidation: React.FC<AdvancedValidationProps> = ({
  rules,
  formData,
  onValidationChange,
  showSuccessStates = true,
  showInlineMessages = true,
  className = '',
  zodSchema
}) => {
  const [validationState, setValidationState] = useState<ValidationState>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const debouncedValidations = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Validate a single field
  const validateField = (field: string, value: any, immediate = false) => {
    const rule = rules.find(r => r.field === field);
    if (!rule) return;

    const performValidation = () => {
      const result = rule.validator(value, formData);
      
      setValidationState(prev => {
        const newState = { ...prev, [field]: result };
        onValidationChange?.(newState);
        return newState;
      });
    };

    // Clear existing debounced validation
    const existingTimeout = debouncedValidations.current.get(field);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    if (immediate || !rule.debounceMs) {
      performValidation();
    } else {
      // Set up debounced validation
      const timeout = setTimeout(performValidation, rule.debounceMs);
      debouncedValidations.current.set(field, timeout);
    }
  };

  // Validate all fields
  const validateAllFields = () => {
    rules.forEach(rule => {
      const value = formData[rule.field];
      validateField(rule.field, value, true);
    });
    // Run Zod validation if provided
    if (zodSchema) {
      const result = zodSchema.safeParse(formData);
      const zodIssues: ValidationState = {};
      if (!result.success) {
        result.error.issues.forEach(issue => {
          const pathKey = (issue.path?.join('.') || 'form') as string;
          zodIssues[pathKey] = {
            isValid: false,
            level: 'error',
            message: issue.message
          };
        });
      }
      setValidationState(prev => ({ ...zodIssues, ...prev }));
      onValidationChange?.({ ...zodIssues, ...validationState });
    }
  };

  // Handle field value changes
  useEffect(() => {
    rules.forEach(rule => {
      if (rule.trigger === 'change' || !rule.trigger) {
        const value = formData[rule.field];
        validateField(rule.field, value);
      }
    });
    // Optionally validate with Zod on change for touched fields
    if (zodSchema) {
      const res = zodSchema.safeParse(formData);
      const zodIssues: ValidationState = {};
      if (!res.success) {
        res.error.issues.forEach(issue => {
          const pathKey = (issue.path?.join('.') || 'form') as string;
          zodIssues[pathKey] = {
            isValid: false,
            level: 'error',
            message: issue.message
          };
        });
      }
      setValidationState(prev => ({ ...prev, ...zodIssues }));
      onValidationChange?.({ ...validationState, ...zodIssues });
    }
  }, [formData, rules]);

  // Mark field as touched
  const markFieldTouched = (field: string) => {
    setTouchedFields(prev => new Set([...prev, field]));
  };

  // Get validation result for a field
  const getFieldValidation = (field: string): ValidationResult | undefined => {
    return validationState[field];
  };

  // Check if field should show validation state
  const shouldShowValidation = (field: string): boolean => {
    const validation = validationState[field];
    if (!validation) return false;
    
    // Always show errors and warnings once validated
    if (validation.level === 'error' || validation.level === 'warning') return true;
    
    // Show success only if enabled and field is touched
    if (validation.level === 'success' && showSuccessStates) {
      return touchedFields.has(field);
    }
    
    return true;
  };

  // Render validation message
  const renderValidationMessage = (field: string) => {
    const validation = getFieldValidation(field);
    if (!validation || !shouldShowValidation(field) || !showInlineMessages) return null;

    const getIcon = () => {
      switch (validation.level) {
        case 'error': return <FiX className="text-red-500" />;
        case 'warning': return <FiAlertTriangle className="text-yellow-500" />;
        case 'success': return <FiCheck className="text-green-500" />;
        case 'info': return <FiInfo className="text-blue-500" />;
        default: return null;
      }
    };

    const getColorClasses = () => {
      switch (validation.level) {
        case 'error': return 'text-red-600 bg-red-50 border-red-200';
        case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
        case 'success': return 'text-green-600 bg-green-50 border-green-200';
        case 'info': return 'text-blue-600 bg-blue-50 border-blue-200';
        default: return 'text-gray-600 bg-gray-50 border-gray-200';
      }
    };

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: 'auto', marginTop: '0.25rem' }}
          exit={{ opacity: 0, height: 0, marginTop: 0 }}
          className={`text-sm border rounded-md p-2 ${getColorClasses()}`}
        >
          <div className="flex items-start gap-2">
            {getIcon()}
            <div className="flex-1">
              {validation.message && (
                <p className="font-medium">{validation.message}</p>
              )}
              {validation.suggestions && validation.suggestions.length > 0 && (
                <ul className="list-disc list-inside mt-1 space-y-0.5 opacity-80">
                  {validation.suggestions.map((suggestion, index) => (
                    <li key={index} className="text-xs">{suggestion}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  };

  // Get input styling based on validation state
  const getInputClassNames = (field: string, baseClasses = '') => {
    const validation = getFieldValidation(field);
    if (!validation || !shouldShowValidation(field)) return baseClasses;

    const validationClasses = {
      error: 'border-red-500 focus:border-red-500 focus:ring-red-500',
      warning: 'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500',
      success: 'border-green-500 focus:border-green-500 focus:ring-green-500',
      info: 'border-blue-500 focus:border-blue-500 focus:ring-blue-500'
    };

    return `${baseClasses} ${validationClasses[validation.level!] || ''}`;
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      debouncedValidations.current.forEach(timeout => clearTimeout(timeout));
      debouncedValidations.current.clear();
    };
  }, []);

  return (
    <div className={className}>
      {/* This component provides validation context and helper functions */}
      <div style={{ display: 'none' }} />
    </div>
  );
};

export default AdvancedValidation;

// Custom hook for using validation functionality
export const useAdvancedValidation = (
  rules: ValidationRule[],
  formData: Record<string, any>
) => {
  const [validationState, setValidationState] = useState<ValidationState>({});
  const [isValidating, setIsValidating] = useState(false);

  const validateField = (field: string, immediate = false) => {
    const rule = rules.find(r => r.field === field);
    if (!rule) return;

    setIsValidating(true);
    const value = formData[field];
    
    setTimeout(() => {
      const result = rule.validator(value, formData);
      setValidationState(prev => ({ ...prev, [field]: result }));
      setIsValidating(false);
    }, immediate ? 0 : rule.debounceMs || 300);
  };

  const validateAll = () => {
    setIsValidating(true);
    const newState: ValidationState = {};
    
    rules.forEach(rule => {
      const value = formData[rule.field];
      newState[rule.field] = rule.validator(value, formData);
    });
    
    setValidationState(newState);
    setIsValidating(false);
  };

  const isFormValid = Object.values(validationState).every(
    result => result.isValid !== false
  );

  const getFieldError = (field: string) => {
    const validation = validationState[field];
    return validation?.level === 'error' ? validation.message : undefined;
  };

  const hasFieldError = (field: string) => {
    const validation = validationState[field];
    return validation?.level === 'error';
  };

  return {
    validationState,
    isValidating,
    validateField,
    validateAll,
    isFormValid,
    getFieldError,
    hasFieldError
  };
};

// Common validation rules
export const commonValidators = {
  required: (message = 'This field is required'): ValidationRule['validator'] => 
    (value) => ({
      isValid: value != null && value !== '' && (!Array.isArray(value) || value.length > 0),
      level: 'error',
      message: value == null || value === '' || (Array.isArray(value) && value.length === 0) ? message : undefined
    }),

  minLength: (min: number, message?: string): ValidationRule['validator'] =>
    (value) => {
      const length = typeof value === 'string' ? value.length : 0;
      return {
        isValid: length >= min,
        level: length < min ? 'error' : 'success',
        message: length < min ? (message || `Must be at least ${min} characters`) : undefined
      };
    },

  maxLength: (max: number, message?: string): ValidationRule['validator'] =>
    (value) => {
      const length = typeof value === 'string' ? value.length : 0;
      return {
        isValid: length <= max,
        level: length > max ? 'error' : 'success',
        message: length > max ? (message || `Must be no more than ${max} characters`) : undefined
      };
    },

  email: (message = 'Please enter a valid email address'): ValidationRule['validator'] =>
    (value) => {
      if (!value) return { isValid: true };
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return {
        isValid: emailRegex.test(value),
        level: emailRegex.test(value) ? 'success' : 'error',
        message: emailRegex.test(value) ? undefined : message
      };
    },

  url: (message = 'Please enter a valid URL'): ValidationRule['validator'] =>
    (value) => {
      if (!value) return { isValid: true };
      try {
        new URL(value);
        return { isValid: true, level: 'success' };
      } catch {
        return { isValid: false, level: 'error', message };
      }
    },

  numeric: (message = 'Please enter a valid number'): ValidationRule['validator'] =>
    (value) => {
      if (!value) return { isValid: true };
      const num = Number(value);
      return {
        isValid: !isNaN(num),
        level: !isNaN(num) ? 'success' : 'error',
        message: !isNaN(num) ? undefined : message
      };
    },

  range: (min: number, max: number, message?: string): ValidationRule['validator'] =>
    (value) => {
      if (!value) return { isValid: true };
      const num = Number(value);
      const inRange = !isNaN(num) && num >= min && num <= max;
      return {
        isValid: inRange,
        level: inRange ? 'success' : 'error',
        message: inRange ? undefined : (message || `Must be between ${min} and ${max}`)
      };
    }
};